import React, { useEffect, useMemo, useState } from "react";
import { Drawer, IconButton, useTheme, Toolbar, AppBar, Box } from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import logo from "../../assets/images/logo.png";
import { INavigationItem } from "../../services/navigation-data-service/Models";
import GroupHierarchy from "../group-hierarchy/desktop/GroupHierarchy";
import { routesByViewID } from "../../routes/routes";
import { UIViewEnum } from "../../common/enums/UIViewEnum";
import {isMobile, isTablet} from 'react-device-detect';
import WithIsLoadingHOC from "../../hocs/is-loading-hoc/IsLoadingHOC";
import useNavigateTo, { NavigateToOptions } from "../../hooks/use-navigate-to/useNavigateTo";
import { NewViewTypeEnum } from "../../common/enums/NewViewTypeEnum";
import { useOfflineDetectContext } from "../../contexts/offline-detect-context/UseOfflineDetectContext";
import { useUserDefaultViews } from "../../queries/user-default-view/UseUserDefaultViews";
import { useQueryNotifications } from "../../queries/UseQueryNotifications";
import { eLynxObjectTypeEnum } from "../../common/enums/eLynxObjectType";
import { useUserContext } from "../../contexts/user-context/UserContext";
import { useEvaluateFeature } from "../../queries/features/UseEvaluateFeature";
import { FeatureConstants } from "../../services/features-data-service/Models";
import NavigationGroupTree from "../navigation/NavigationGroupTree";
import PushPinButton from "../push-pin-button/PushPinButton";
import { useLocation } from "react-router";


const GroupHierarchyWithLoading = WithIsLoadingHOC(GroupHierarchy);

export type HierarchyDrawerProps = {
    open: boolean;
    pinned?: boolean;
    onPinClick?: (() => void) | undefined;
    onClose?: () => void;
}

export default function HierarchyDrawer({ 
    open,
    pinned, 
    onPinClick,
    onClose 
}: HierarchyDrawerProps) {

    const [loading, setLoading] = useState(true);    
    const navigateTo = useNavigateTo();
    const [offlineState] = useOfflineDetectContext();    
    const [userState] = useUserContext();
    const theme = useTheme();
    const location = useLocation();
    //const isDesktop = useMediaQuery(theme.breakpoints.up("sm"));
    const isPhone = isMobile && !isTablet;


    const { data: defaultViews } = useQueryNotifications(useUserDefaultViews)({
        enabled: !offlineState.offline
    });

    const { data: navigationV2Feature } = useQueryNotifications(useEvaluateFeature)({
        feature: FeatureConstants.NavigationV2,
    });

    const navigationV2 = (navigationV2Feature ?? false);


    // const groupViewID = useMemo(() => {

    //     return defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && 
    //         o.user?.id === userState.user?.id)?.viewID ?? 
    //         defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && 
    //             !o.user?.id)?.viewID ??
    //         UIViewEnum.Group_SummaryListing;
    // }, [
    //     defaultViews,
    //     userState.user?.id
    // ]);

    //const groupViewID = useMemo(() => {

    function groupViewID(groupID: number) {
        const vid = defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && o.user?.id === userState.user?.id && !o.objectID)?.viewID ?? 
                defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && o.objectID === groupID && o.user?.id === userState.user?.id)?.viewID ?? 
                defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && o.objectID === groupID && !o.user?.id)?.viewID ?? 
                defaultViews?.find(o => o.objectType === eLynxObjectTypeEnum.Group && !o.user?.id && !o.objectID)?.viewID ??
            UIViewEnum.Group_SummaryListing;

        return vid;
    }
    // }, [
    //     defaultViews,
    //     userState.user?.id,
    //     routeContext.groupID
    // ]);
    const groupRoute = useMemo(() => {
        const route = routesByViewID.get(UIViewEnum.Group_SummaryListing);
        return route;
    }, [
        groupViewID
    ]);


    const onSelected = (item: INavigationItem, shiftKey: boolean, ctrlKey: boolean): void => {

        let options: NavigateToOptions | undefined;

        if (shiftKey || ctrlKey) {
            options = {
                newViewType: shiftKey ? NewViewTypeEnum.NewWindow : NewViewTypeEnum.NewTab
            }
        }

        const path = routesByViewID.get(groupViewID(item.objectID))?.fullPath;
        navigateTo(`${path}?groupID=${item.objectID}`, options);

        if(!pinned){
            onClose?.();
        }
    };

     useEffect(() => {
    
            const scrollToSelected = () => {
                // Scope the search to the tree structure
                const selectedNode = document.querySelector('#main-grp-tree [role="tree"] .Mui-selected');
                
                if (selectedNode) {
                    selectedNode.scrollIntoView({
                    behavior: 'smooth',
                    //block: 'nearest', // Ensures it scrolls only as much as needed
                    block: 'center',
                    });
                }
            };        
    
            if (open) {
                setTimeout(() => {
                    scrollToSelected();
                }, 100);
            }
    
    },[
        open,
        location.pathname
    ]);    

    return (
        <Drawer style={{height: "100vh"}}
            variant={!isPhone ? "permanent" : "temporary"}
             open={open}
            slotProps={{
                paper: {
                    sx : {
                        position: "relative",
                        whiteSpace: "nowrap",
                        maxWidth: theme.spacing(50),
                        [theme.breakpoints.down('xs')]: {
                            maxWidth: '100%'
                        },
                        transition: theme.transitions.create("width", {
                            easing: theme.transitions.easing.sharp,
                            duration: open ? theme.transitions.duration.enteringScreen : theme.transitions.duration.leavingScreen,
                        }),
                        width: !open ? theme.spacing(7) : 'auto',
                        //overflowX: !open ? 'hidden' : 'auto'
                        overflow: 'hidden'
                    }
                }
            }}>
            {
                // Only need to display this when drawer is temporary
                isPhone &&
                <AppBar position="absolute">
                    <Toolbar>
                        <IconButton
                            edge="start"
                            color="inherit"
                            aria-label="open drawer"
                            onClick={() => { onClose && onClose(); }}>
                            <MenuIcon />
                        </IconButton>
                        <img src={logo} alt="eLynx Technologies" />
                    </Toolbar>
                </AppBar>
            }
            <Toolbar/>
            <Box 
                sx={{
                    // marginTop: theme.spacing(1),
                    height: '100%',
                    [theme.breakpoints.up("sm")]: {
                        width: theme.spacing(50),
                    },
                    position: 'relative',
                    overflow: navigationV2 ? 'hidden' : 'auto'
                }}>
                
                <Box sx={{display: {xs: "none", sm: "block"}, height: 1}}>
                {/* <Hidden only="xs" > */}
                { navigationV2 ? 

                <NavigationGroupTree
                    id="main-grp-tree"
                    route={groupRoute}
                    actions={
                        <PushPinButton onClick={onPinClick} pinned={pinned} />
                    }
                    onClick={(item, route, shiftKey, ctrlKey) => {onSelected(item, shiftKey, ctrlKey)}}
                 />

                :
                    <GroupHierarchyWithLoading
                        isLoading={loading}
                        onLoading={setLoading}                    
                        onSelected={onSelected}
                        enableDynamicGroupRefresh={true} />


                }
                {/* </Hidden> */}
                </Box>

                {/* <Box sx={{display: {xs: "block", sm: "none"}}}>
                    <MobileGroupHierarchyWithLoading 
                        isLoading={loading}
                        onLoading={setLoading}                    
                        onSelected={onSelected} />
                </Box> */}
            </Box>
            {/* <Divider /> */}
        </Drawer >
    );
}
